import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { flipCamera } from '@streem/sdk-core';
import { AppIcon, styled, useTheme } from '@streem/ui-react';
import { useCallback, useEffect, useRef, useState, forwardRef, useLayoutEffect, } from 'react';
import { CameraSubject, CameraSubjectResponseStatus } from 'streem-sdk-protobuf';
import { useObservable } from '../../..';
import { FlipCameraButton } from './camera_flip/flip_camera_button';
import { ScreenshareButton } from './screenshare/screenshare_button';
import { UserAudioStatus } from './user_audio_status';
import { LoadingScreen } from './loading_screen';
import { CameraInactiveScreen } from './camera_inactive_screen';
import { useMultiplexer } from '../../../hooks/use_multiplexer';
import { useRoomSession } from '../../../context/room_session';
import { useDetermineUIMode } from '../../../hooks/use_determine_ui_mode';
import { useCanvasOrientation } from '../../../hooks/use_canvas_orientation';
import { getAspectRatioConstraintDims } from '../../../util/get_aspect_ratio_constraint_dims';
import { useCompanySettings } from '../../../context/company_settings';
/** how long we wait for a camera subject request before timing it out if there is no response from mobile */
const CAM_SUBJECT_REQUEST_TIMEOUT = 1000 * 15;
export var CameraRequestState;
(function (CameraRequestState) {
    CameraRequestState[CameraRequestState["INITIAL"] = 0] = "INITIAL";
    CameraRequestState[CameraRequestState["PENDING"] = 1] = "PENDING";
    CameraRequestState[CameraRequestState["REQUEST_DENIED"] = 2] = "REQUEST_DENIED";
    CameraRequestState[CameraRequestState["REQUEST_TIMEOUT"] = 3] = "REQUEST_TIMEOUT";
})(CameraRequestState || (CameraRequestState = {}));
export const VideoFrame = forwardRef(({ cardOptions = {
    constrainContainerToCanvas: false,
    localPipView: false,
}, animationOptions = {
    duration: 0,
    delay: 0,
}, videoController, showArrow, flipArrow, }, ref) => {
    const { styles, constrainContainerToCanvas, localPipView } = cardOptions;
    const { animating, duration: animationDuration, delay: animationDelay } = animationOptions;
    const theme = useTheme();
    const roomSession = useRoomSession();
    const { faceToFaceModeEnabled, screenshareEnabled } = useCompanySettings();
    const [cameraRequestState, setCameraRequestState] = useState(CameraRequestState.INITIAL);
    const [cameraSubject] = useObservable(videoController.cameraSubject);
    const [responses] = useObservable(roomSession.camera.responses);
    const responsesRef = useRef(responses);
    useEffect(() => {
        responsesRef.current = responses;
    }, [responses]);
    const [latestRemoteCameraSubjectThatIsNotScreenshare] = useObservable(roomSession.camera.latestRemoteCameraSubjectThatIsNotScreenshare);
    const [cameraSubjectChangeRequestId, setCameraSubjectChangeRequestId] = useState(null);
    const [latestCameraSubjectChangeResponse] = useObservable(roomSession.camera.latestResponse);
    const [latestCameraSubjectChangeRequest] = useObservable(roomSession.camera.latestRequest);
    // Feed what we know about the current display layout into useDetermineUIMode, and it'll tell us what display mode to use.
    const determinedUIProperties = useDetermineUIMode({
        videoController,
    });
    const clientCapabilities = useObservable(roomSession.room.remoteParticipantCapabilities);
    useEffect(() => {
        // ensure the user is prompted before the tab closes
        const handleBeforeUnload = (e) => {
            // Chrome requires this value to be set
            e.returnValue = true;
            // returning a string is the only way to ensure the prompt happens on all other browsers
            return '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        // make sure we remove this listener so the user is no longer prompted once a call is complete
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    });
    useEffect(() => {
        if (cameraSubjectChangeRequestId ===
            latestCameraSubjectChangeResponse?.inResponseToWallItemId) {
            if (
            // legacy versions of mobile apps may not set status, so check accepted too
            latestCameraSubjectChangeResponse?.accepted ||
                latestCameraSubjectChangeResponse?.status ===
                    CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ACCEPTED) {
                setCameraRequestState(CameraRequestState.INITIAL);
            }
            else if (latestCameraSubjectChangeResponse?.status ===
                CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ABORTED) {
                setCameraRequestState(CameraRequestState.REQUEST_TIMEOUT);
            }
            else if (
            // legacy versions of mobile apps may not set status, so check accepted==false
            !latestCameraSubjectChangeResponse?.accepted ||
                latestCameraSubjectChangeResponse?.status ===
                    CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_REJECTED) {
                setCameraRequestState(CameraRequestState.REQUEST_DENIED);
            }
            else {
                // Print Error Modal on unexpected response
                setCameraRequestState(CameraRequestState.REQUEST_TIMEOUT);
            }
        }
    }, [latestCameraSubjectChangeResponse, cameraSubjectChangeRequestId]);
    const [freezeFrameImgSrc, setFreezeFrameImgSrc] = useState(undefined);
    useEffect(() => {
        const enabledSubscription = videoController.enabled.subscribe(enabled => {
            if (!enabled && localPipView && canvasContainer && canvasContainer.current) {
                const remoteCanvas = canvasContainer.current.querySelector('canvas');
                if (remoteCanvas) {
                    // take a snapshot of the remote video canvas
                    // use as a freeze frame while video feed is turning off
                    const canvasDataURL = remoteCanvas.toDataURL('image/jpeg', 0.4);
                    setFreezeFrameImgSrc(canvasDataURL);
                }
            }
            else {
                setFreezeFrameImgSrc(undefined);
            }
        });
        return () => {
            enabledSubscription.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoController]);
    useEffect(() => {
        if ((latestCameraSubjectChangeResponse?.accepted ||
            CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ACCEPTED) &&
            canvasContainer &&
            canvasContainer.current &&
            !videoController.ownerIsLocalUser) {
            const remoteCanvas = canvasContainer.current.querySelector('canvas');
            if (remoteCanvas) {
                // take a snapshot of the remote video canvas
                // use as a freeze frame while video feed is switching and animating
                const canvasDataURL = remoteCanvas.toDataURL('image/jpeg', 0.4);
                setFreezeFrameImgSrc(canvasDataURL);
                // catch all remove frozen frame
                setTimeout(() => {
                    setFreezeFrameImgSrc(undefined);
                }, (animationDelay || 0) + animationDuration * 2);
            }
        }
        return () => {
            if (freezeFrameImgSrc) {
                setFreezeFrameImgSrc(undefined);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestCameraSubjectChangeResponse]);
    // clean up freeze frame image when animation is over
    useEffect(() => {
        if (!animating && freezeFrameImgSrc) {
            setFreezeFrameImgSrc(undefined);
        }
        if (animating && freezeFrameImgSrc) {
            // the canvas image is flipped during the animated transition
            // this timeout removes the freeze frame image halfway through that animation, which is what we want for the animation
            setTimeout(() => {
                setTimeout(() => {
                    setFreezeFrameImgSrc(undefined);
                }, animationDuration / 2);
            }, animationDelay);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animating, animationDuration]);
    /** Multiplexer */
    const canvasContainer = useRef(null);
    const { width: canvasWidth, height: canvasHeight } = useMultiplexer(roomSession, videoController, canvasContainer) ?? { width: 1280, height: 720 };
    // While faceToFaceModeEnabled determines if this is a 2WV call and if we should show audio controls on the video frame,
    // this is to place the camera flip button only on the remote video so local users cannot use this button to flip the camera on their video frame
    const canRequestCameraSubjectChange = !videoController.ownerIsLocalUser;
    const timerRef = useRef(null);
    const handleRequestCameraFlip = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        const requestedCameraSubject = flipCamera(cameraSubject);
        const requestId = roomSession.camera.requestCameraSubjectChange(requestedCameraSubject);
        setCameraSubjectChangeRequestId(requestId);
        setCameraRequestState(CameraRequestState.PENDING);
        // If we don't get a response from mobile after X seconds, we'll timeout the request by responding to it ourselves with an abort status
        timerRef.current = setTimeout(() => {
            // NOTE: needed to use ref to avoid stale closure on responses
            const response = responsesRef.current?.find(r => r.inResponseToWallItemId === requestId);
            if (!response) {
                roomSession.camera.abortCameraSubjectChangeRequest(requestId);
                setCameraSubjectChangeRequestId(null);
                setCameraRequestState(CameraRequestState.REQUEST_TIMEOUT);
            }
        }, CAM_SUBJECT_REQUEST_TIMEOUT);
    };
    const resetToInitialCameraState = () => {
        setCameraRequestState(CameraRequestState.INITIAL);
    };
    const handleExitScreenshareRequest = () => {
        const requestId = roomSession.camera.requestCameraSubjectChange(latestRemoteCameraSubjectThatIsNotScreenshare === undefined
            ? CameraSubject.CAMERA_SUBJECT_PERSON
            : latestRemoteCameraSubjectThatIsNotScreenshare);
        setCameraSubjectChangeRequestId(requestId);
        setCameraRequestState(CameraRequestState.PENDING);
    };
    const handleRequestScreenShare = useCallback(() => {
        const requestId = roomSession.camera.requestCameraSubjectChange(CameraSubject.CAMERA_SUBJECT_SCREEN);
        setCameraSubjectChangeRequestId(requestId);
        setCameraRequestState(CameraRequestState.PENDING);
    }, [roomSession]);
    // Only show screenshare button for remote user who has screenshare capabilities
    const canRequestScreenshare = !videoController.ownerIsLocalUser &&
        (clientCapabilities[0]?.canSendCameraSubjects ?? []).includes(CameraSubject.CAMERA_SUBJECT_SCREEN) &&
        screenshareEnabled;
    const Arrow = ({ flipArrow, }) => (_jsx(ArrowContainer, { flipArrow: flipArrow, children: _jsx(AppIcon, { name: "ArrowLongIcon", color: "white", style: {
                transform: flipArrow ? 'scaleX(-1)' : '',
            } }) }));
    const dataTestId = canRequestCameraSubjectChange
        ? 'video-frame-test-id-remote'
        : 'video-frame-test-id-local';
    const canvasAspectRatioConversion = useCanvasOrientation(canvasWidth, canvasHeight);
    return (_jsx(_Fragment, { children: _jsxs(VideoContainer, { styles: styles, theme: theme, "data-testid": dataTestId, ref: ref, animating: animating, children: [_jsxs(CanvasWrapper, { "data-testid": "canvas-wrapper", "data-queryselector": "canvas-wrapper", ref: canvasContainer, children: [_jsx(FreezeFrameImg, { src: freezeFrameImgSrc, alt: "video tranistion freeze frame", style: {
                                visibility: freezeFrameImgSrc !== undefined ? 'visible' : 'hidden',
                            }, "data-clarity-mask": "true" }), _jsx(CameraInactiveScreen, { determinedUIProperties: determinedUIProperties, videoController: videoController, constrainContainerToCanvas: animating || constrainContainerToCanvas, aspectRatioConversion: canvasAspectRatioConversion })] }), _jsx(LoadingScreen, { videoController: videoController }), canRequestCameraSubjectChange &&
                    (faceToFaceModeEnabled || canRequestScreenshare) && (_jsxs(Constrainer, { constrainContainerToCanvas: constrainContainerToCanvas && !animating, canvasDims: { width: canvasWidth, height: canvasHeight }, animating: animating, children: [faceToFaceModeEnabled && (_jsx(UserAudioStatus, { videoController: videoController })), _jsxs(ButtonContainer, { children: [faceToFaceModeEnabled &&
                                    cameraSubject !== CameraSubject.CAMERA_SUBJECT_SCREEN && (_jsxs(_Fragment, { children: [_jsx(FlipCameraButton, { isCameraFlipRequest: latestCameraSubjectChangeRequest?.requestedCameraSubject ===
                                                CameraSubject.CAMERA_SUBJECT_PERSON ||
                                                latestCameraSubjectChangeRequest?.requestedCameraSubject ===
                                                    CameraSubject.CAMERA_SUBJECT_WORLD ||
                                                false, cameraSubject: cameraSubject, cameraRequestState: cameraRequestState, onRequest: () => {
                                                handleRequestCameraFlip();
                                            }, onCancel: resetToInitialCameraState }), showArrow && _jsx(Arrow, { flipArrow: flipArrow })] })), canRequestScreenshare && (_jsx(ScreenshareButton, { isScreenShareRequest: latestCameraSubjectChangeRequest?.requestedCameraSubject ===
                                        CameraSubject.CAMERA_SUBJECT_SCREEN || false, cameraSubject: cameraSubject, cameraRequestState: cameraRequestState, onEnterScreenshareRequest: () => {
                                        handleRequestScreenShare();
                                    }, onExitScreenshareRequest: handleExitScreenshareRequest, onCancel: resetToInitialCameraState }))] })] }))] }) }));
});
VideoFrame.displayName = 'VideoFrame';
const FreezeFrameImg = styled.img `
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    object-fit: contain;
    background: inherit;
    border-radius: 20px;
`;
const CanvasWrapper = styled.div(() => {
    return {
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        borderRadius: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // Needed for rounded corners to work with desktop Safari.
        backfaceVisibility: 'hidden',
        transform: 'translate3d(0,0,0)',
        zIndex: 1,
    };
});
const ButtonContainer = styled.div `
    position: absolute;
    top: 10px;
    right: 20px;
    z-index: 12;
    display: flex;
    flex-direction: column;
`;
const Constrainer = ({ animating, canvasDims, constrainContainerToCanvas, children, }) => {
    const { width: canvasWidth, height: canvasHeight } = canvasDims;
    const constrainerRef = useRef(null);
    const [parentDims, setParentDims] = useState({
        width: 0,
        height: 0,
    });
    // Adds a resize listener to the parent container.
    // When the parent changes size, the updated dimensions are updated in state
    // We only pay attention to the parent dims when 'constrainContainerToCanvas' is true
    // Otherwise, we let the constrainer occupy 100% width/height of the parent container
    useLayoutEffect(() => {
        const parentResizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const resizedParentDims = {
                    width: entry.contentRect.width,
                    height: entry.contentRect.height,
                };
                setParentDims(resizedParentDims);
            }
        });
        if (constrainerRef &&
            constrainerRef.current &&
            constrainerRef.current.parentElement &&
            constrainContainerToCanvas) {
            parentResizeObserver.observe(constrainerRef.current.parentElement);
        }
        if (constrainerRef &&
            constrainerRef.current &&
            constrainerRef.current.parentElement &&
            !constrainContainerToCanvas) {
            parentResizeObserver.unobserve(constrainerRef.current.parentElement);
        }
        return () => parentResizeObserver.disconnect();
    }, [constrainContainerToCanvas]);
    useLayoutEffect(() => {
        const constraintDims = getAspectRatioConstraintDims(canvasWidth, canvasHeight, parentDims.width, parentDims.height);
        if (constrainerRef && constrainerRef.current && constrainContainerToCanvas) {
            // we only need to constrain the width, since we want the UI indicators anchored to the top and bottom of the parent container
            constrainerRef.current.style.setProperty('--aspect-ratio-width', `${constraintDims.width}px`);
        }
        if (constrainerRef && constrainerRef.current && !constrainContainerToCanvas) {
            // remove custom width property to enable 'width: 100%' fallback value
            constrainerRef.current.style.removeProperty('--aspect-ratio-width');
        }
        // eslint-disable-next-line
    }, [parentDims, canvasDims, constrainContainerToCanvas]);
    return (_jsx("div", { style: {
            transition: 'opacity 100ms ease-out',
            opacity: animating ? 0 : 1,
            width: 'var(--aspect-ratio-width, 100%)',
            height: '100%',
            position: 'absolute',
        }, ref: constrainerRef, children: children }));
};
const VideoContainer = styled.div(props => {
    // These are default styles for stacked view. Prop styles will override when in face to world.
    return {
        width: 'var(--item-width, 100%)',
        height: `var(--item-height, 100%)`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        borderRadius: '20px',
        backgroundColor: props.theme.colors.grey90,
        ...props.styles,
    };
});
const ArrowContainer = styled.span(({ flipArrow }) => {
    return {
        position: 'absolute',
        right: flipArrow ? '48px' : '-28px',
        marginTop: '12px',
        top: '0',
        zIndex: 3,
    };
});
//# sourceMappingURL=video_frame.js.map